/**
 * claude-regional-fixes (issue 3 follow-up) — THE canonical initial (first)
 * sequence number for a new regulatory activity, by region.
 *
 * Most markets initialise at ICH's 0000. TGA and FDA are the documented
 * exceptions. TGA (D-090, Sharon 2026-08-18): TGA reserves 0000 for a *baseline*
 * sequence, so the first sequence of a regulatory activity is **0001**:
 *
 *   "we would not expect the sequence number 0000 to be used unless submitting
 *    a baseline sequence. The initial sequence of a regulatory activity should
 *    always be 0001."
 *   — eCTD AU module 1 and regional information, Specification and guidance for
 *     use, v3.1 (September 2017), Sequence number section, p. 28.
 *
 * FDA (policy batch 2, item 1): new FDA applications also default to 0001.
 *
 *   "It must be a unique number with a maximum of four (4)-numeric digits,
 *    should start at 0001, and should not exceed 9999."
 *   — FDA eCTD Module 1 specification v2.6 (2025-03-31), III.B.2.b, p. 7
 *     (archived: outputs/validation-engine-fixes/research/fda-m1-v2.6.pdf).
 *
 * This is a CREATE-TIME DEFAULT only. Existing FDA applications that started at
 * 0000 are valid history and are never renumbered — published sequences are
 * immutable — and FDA-SEQ-INIT-01 accepts their 0000 anchor.
 *
 * The regional initial-sequence rules (FDA/TGA/EMA/MHRA/HC/CH/PMDA-SEQ-INIT-01)
 * read the same helper as their recommended anchor; this default keeps a new
 * original from being silently stored at the DB-default 0000. Region is
 * normalised via regionCode() so 'Health Canada'/'HC' etc. resolve.
 *
 * Other existing region defaults, including PMDA, remain unchanged. This
 * helper does not establish whether a region currently accepts eCTD 3.2.2.
 */
import { regionCode } from '../regions.js';

export function initialSequenceNumberForRegion(region: string | null | undefined): '0000' | '0001' {
  const code = regionCode(region);
  return code === 'TGA' || code === 'FDA' ? '0001' : '0000';
}

/** Existing callers share the same canonical regional default. */
export const initialSequenceNumber = initialSequenceNumberForRegion;
